import { Link, useLocation } from 'react-router-dom';

interface BreadcrumbSegment {
  label: string;
  path: string;
}

/** Map static route paths to human-readable labels. */
const ROUTE_LABELS: Record<string, string> = {
  '/': 'Dashboard',
  '/users': 'Users',
  '/classrooms': 'Classrooms',
  '/content': 'Content Moderation',
  '/analytics': 'Analytics',
  '/billing': 'Billing',
  '/ai': 'AI Tutor Oversight',
  '/translations': 'Translations',
  '/system': 'System Config',
  '/notifications': 'Notifications',
  '/admin-accounts': 'Admin Accounts',
  '/impersonation': 'Impersonation & Debug',
};

/**
 * Derives breadcrumb segments from the current URL path.
 * e.g. /users/abc123 → Dashboard / Users / abc123
 */
function buildSegments(pathname: string): BreadcrumbSegment[] {
  const segments: BreadcrumbSegment[] = [{ label: 'Dashboard', path: '/' }];

  if (pathname === '/') return segments;

  const parts = pathname.split('/').filter(Boolean);
  let accumulated = '';

  for (const part of parts) {
    accumulated += `/${part}`;
    const label = ROUTE_LABELS[accumulated] ?? part;
    segments.push({ label, path: accumulated });
  }

  return segments;
}

export function Breadcrumb() {
  const location = useLocation();
  const segments = buildSegments(location.pathname);

  if (segments.length <= 1) return null;

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm text-gray-500 min-w-0">
      {segments.map((seg, idx) => {
        const isLast = idx === segments.length - 1;
        return (
          <span key={seg.path} className="flex items-center gap-1 min-w-0">
            {idx > 0 && <span className="text-gray-300 flex-shrink-0">/</span>}
            {isLast ? (
              <span className="text-gray-900 font-medium truncate max-w-[160px]">{seg.label}</span>
            ) : (
              <Link
                to={seg.path}
                className="hover:text-gray-700 truncate max-w-[120px] transition-colors"
              >
                {seg.label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}
